import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  Eyebrow,
  Lead,
  DirectAnswer,
  PrimaryLink,
  TextLink,
} from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";
import { ogImageMeta, ogImageUrl } from "@/lib/og";
import { articleSchema, jsonLdScript } from "@/lib/schema";
import { contactFunnel } from "@/lib/analytics";
import { RelatedLinks } from "@/components/site/RelatedLinks";

const PATH = "/fractional-cto-vs-full-time-vs-agency";
const CTA_SOURCE_HERO = "fcto_compare_hero";
const CTA_SOURCE_BAND = "fcto_compare_footer";
const REVIEWED = "3 August 2026";

const ANSWER =
  "A fractional CTO gives you senior technical judgement part-time, paid by cadence rather than salary. A full-time CTO owns technology outright and costs a loaded executive package. An agency supplies build capacity against a specification and does not own the strategy behind it.";

const title = "Fractional CTO vs Full-Time vs Agency: Cost and Fit";
const description =
  "Compare cost shape, speed, ownership, incentives and continuity risk across the three options, and see which one fits the stage you are at.";

type Row = {
  criterion: string;
  fractional: string;
  fulltime: string;
  agency: string;
};

const ROWS: Row[] = [
  {
    criterion: "What you are buying",
    fractional: "Senior technical judgement, part-time",
    fulltime: "Day-to-day technical leadership, full-time",
    agency: "Build capacity against a specification",
  },
  {
    criterion: "Cost shape",
    fractional: "Variable, scaled to cadence and scope",
    fulltime: "Fixed: salary, equity, employer costs, recruitment fee",
    agency: "Project or day-rate, scales with build volume",
  },
  {
    criterion: "Time to start",
    fractional: "Days to weeks",
    fulltime: "Typically a multi-month executive search",
    agency: "Weeks, subject to their pipeline",
  },
  {
    criterion: "Ownership of the decision",
    fractional: "Shares it: advises, decides with you, holds others to account",
    fulltime: "Owns it outright",
    agency: "Owns delivery of the spec, not the strategy behind it",
  },
  {
    criterion: "Incentive alignment",
    fractional: "No build team to keep busy, no supplier referrals",
    fulltime: "Fully aligned with the company; can become invested in own past decisions",
    agency: "Revenue rises with hours built, so scope tends to grow",
  },
  {
    criterion: "Continuity risk",
    fractional: "Deliberately temporary; knowledge must be written down",
    fulltime: "Low while they stay; high key-person risk if they leave",
    agency: "Concentrated in the supplier relationship and their staff turnover",
  },
  {
    criterion: "Best when",
    fractional: "Decisions are executive-level but the headcount is not yet justified",
    fulltime: "The technical org is large enough to need daily leadership",
    agency: "The what is settled and you need hands to build it",
  },
];

const CHOOSE = [
  {
    title: "Choose a full-time CTO when the org needs daily leadership",
    body: "Once there is a technical team big enough that someone must run it every day, hiring, line management, architecture decisions that cannot wait for a scheduled call, a fractional arrangement is the wrong shape. Say so early: hiring one late is more expensive than hiring one on time.",
  },
  {
    title: "Choose an agency when the specification is genuinely settled",
    body: "If you know what needs building, who it is for, and how you will judge it, an agency is efficient. The failure mode is using one to work out what to build, because the incentive runs towards more build, not less.",
  },
  {
    title: "Choose a fractional CTO when the judgement gap is the problem",
    body: "Not enough work for an executive salary, too much risk to leave to trust. Common triggers: an outsourced team nobody is holding to account, a roadmap the board cannot verify, an investment round putting the technology under scrutiny.",
  },
  {
    title: "Combining them is normal",
    body: "A fractional CTO holding an agency to what it promised is a common and sensible pairing. So is a fractional CTO running the hiring process for the full-time CTO who will replace them.",
  },
];

const FAQ = [
  {
    q: "What is a fractional CTO?",
    a: "An experienced chief technology officer who works with your company part-time rather than as a full-time hire. You get senior technical judgement on the decisions that carry real cost, architecture, delivery, hiring, suppliers and spend, without the salary, equity and hiring risk of an executive appointment.",
  },
  {
    q: "Is a fractional CTO cheaper than a full-time CTO?",
    a: "Usually, but that is the wrong way to frame it. A fractional arrangement costs less because you buy less time, not because the hourly judgement is cheaper. If the business needs full-time technical leadership, fractional is not a discount on it. It is a different thing.",
  },
  {
    q: "What does a fractional CTO cost?",
    a: "No credible published dataset exists for fractional executive rates, which is why this page quotes none. What you can compare is cost shape: an agency bills against build volume, a full-time hire fixes salary, equity and employer cost regardless of the month, and a fractional arrangement is scoped to a cadence you can review.",
  },
  {
    q: "Can a fractional CTO manage our development agency?",
    a: "Yes, and it is one of the most common reasons founders call. Someone independent has to read what the agency promised against what it shipped, and that person should not be paid by the volume of work built.",
  },
  {
    q: "How is a fractional CTO different from a non-executive director?",
    a: "A fractional CTO works inside the business, owning and shaping technical decisions. A non-executive director sits outside it, on the board, holding the executive to account and giving independent assurance. They are different roles with different legal standing.",
  },
];

export const Route = createFileRoute("/fractional-cto-vs-full-time-vs-agency")({
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "fractional-cto-vs-full-time-vs-agency",
        "Guide: fractional CTO vs full-time vs agency",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        articleSchema({
          path: PATH,
          headline: title,
          description,
          datePublished: "2026-08-03",
          dateModified: "2026-08-05",
          image: ogImageUrl("fractional-cto-vs-full-time-vs-agency"),
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
            {
              "@type": "ListItem",
              position: 2,
              name: "Fractional CTO",
              item: `${SITE.url}/what-i-do`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Fractional CTO vs full-time vs agency",
              item: `${SITE.url}${PATH}`,
            },
          ],
        }),
      },
    ],
  }),
  component: ComparePage,
});

function ComparePage() {
  return (
    <div>
      <section
        aria-labelledby="cmp-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Guide</Eyebrow>
          <h1
            id="cmp-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Fractional CTO, full-time CTO or a development agency?
          </h1>
          <DirectAnswer className="mt-6">{ANSWER}</DirectAnswer>
          <Lead className="mt-6">
            Three different answers to three different problems. This is how they actually differ on
            cost shape, speed, ownership, incentives and continuity risk, including the cases where
            a fractional CTO is the wrong choice.
          </Lead>
          <div className="mt-8">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="cmp-table-heading">
        <Container>
          <h2
            id="cmp-table-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Fractional CTO vs full-time CTO vs agency, side by side
          </h2>
          <div className="mt-8 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[720px] border-collapse text-left text-[15px]">
              <caption className="sr-only">
                Comparison of a fractional CTO, a full-time CTO and a development agency across
                seven criteria
              </caption>
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Criterion
                  </th>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Fractional CTO
                  </th>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Full-time CTO
                  </th>
                  <th scope="col" className="border-b border-border px-5 py-4 font-semibold">
                    Development agency
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.criterion} className="align-top">
                    <th
                      scope="row"
                      className="border-b border-border px-5 py-4 font-semibold text-ink"
                    >
                      {row.criterion}
                    </th>
                    <td className="border-b border-border px-5 py-4 text-body">{row.fractional}</td>
                    <td className="border-b border-border px-5 py-4 text-body">{row.fulltime}</td>
                    <td className="border-b border-border px-5 py-4 text-body">{row.agency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 max-w-[70ch] text-[15px] leading-relaxed text-muted-foreground">
            This table reflects twenty years of running these engagements from all three sides, as a
            full-time CTO and COO, as a founder of a consultancy, and as the person brought in to
            hold agencies to account. It is stated as experience, not as survey data.
          </p>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="cmp-choose-heading">
        <Container>
          <Eyebrow>How to choose</Eyebrow>
          <h2
            id="cmp-choose-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Which one you actually need
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {CHOOSE.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="cmp-cost-heading">
        <Container>
          <h2
            id="cmp-cost-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            On cost, and what is not knowable
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              There is no reliable public benchmark for fractional CTO rates. The role has no
              standard definition, engagements vary from a two-week audit to years of weekly
              involvement, and the figures circulating online are marketing material rather than
              survey data. Anyone quoting you a single market rate is guessing.
            </p>
            <p>
              What is knowable is the shape of the cost. A full-time appointment carries salary,
              equity, employer costs and a recruitment fee, and is slow to unwind if the fit is
              wrong. An agency cost scales with how much gets built. A fractional cost scales with
              how much of my time you buy, and stops when you stop.
            </p>
            <p>
              Board-level fee data is better documented for non-executive directors than for
              fractional executives, the sources for that are listed on the{" "}
              <TextLink to="/what-a-technology-non-executive-director-does">
                technology NED guide
              </TextLink>
              .
            </p>
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="cmp-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="cmp-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Common questions
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-7"
            paths={["/interim-cto-vs-fractional-cto", "/when-to-hire-your-first-cto"]}
          />
          <RelatedLinks
            className="mt-8"
            label="Fractional CTO by region"
            variant="cards"
            paths={["/fractional-cto-uk", "/fractional-cto-us", "/fractional-cto-southeast-asia"]}
          />
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="cmp-sources-heading">
        <Container>
          <h2 id="cmp-sources-heading" className="text-xl font-bold tracking-[-0.02em]">
            Sources and review
          </h2>
          <p className="mt-4 max-w-[70ch] text-[15px] leading-relaxed text-body">
            This guide makes no statistical claims. The comparison is drawn from direct experience
            of all three arrangements, and is labelled as such above. Where a figure would normally
            be quoted, market rates for fractional executives, no credible published dataset exists,
            and this page says so rather than repeating a number from a competitor&rsquo;s marketing
            page.
          </p>
          <p className="mt-4 text-[15px] text-muted-foreground">
            Last reviewed: <time dateTime="2026-08-03">{REVIEWED}</time>
          </p>
        </Container>
      </section>

      <CtaBand
        heading="Not sure which one you need?"
        body="Tell me what is happening and I will tell you plainly which of the three fits, even when the answer is not me."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
